import { put, takeEvery, select, race, all } from 'redux-saga/effects';
import { path, mergeAll } from 'ramda';
import { changeItemInStatuses } from 'utilities/fhir/resources';
import {
  resourcesInitialize,
  requestResources,
  resourcesLoadData,
  resourcesLoadDataSuccess,
  resourcesLoadDataFailure,
  requestGetResourceImage,
  requestGetResourceFile,
  resourcesResetFiles,
  resourcesOpenPdf,
  resourcesInitializeComplete,
  requestGetPatientsResourcesStatuses,
  resourcesLikeResource,
  resourcesReadResource,
  requestPutPatientsStatuses,
  resourcesSetStatuses,
  resourcesChangeStatuses,
  resourcesChangeStatusesComplete,
  requestResourcesFaq,
} from 'store/actionCreators/resources';
import moment from 'moment';
import i18n from 'services/i18n';
import { getUserId, getUserPreferedLanguageCode } from 'store/selectors/user';
import { b64toBlob } from 'utilities';

import {
  getDocumentFileUrlById,
  getDocumentImageUrlById,
  getResourceFile,
  getCurrentResource,
  getStatuses,
  isStatusesInChange,
} from 'store/selectors/resources';

import { createSagaRequestRace } from 'utilities/request';

export function* onResourcesInitialize() {
  const lang = yield select(getUserPreferedLanguageCode);

  yield put(requestResources());
  yield put(requestResourcesFaq({ lang }));

  const patientId = yield select(getUserId);
  yield put(requestGetPatientsResourcesStatuses({ patientId }));

  yield all({
    getResources: race(createSagaRequestRace(requestResources)),
    getFaq: race(createSagaRequestRace(requestResourcesFaq)),
    getStatuses: race(createSagaRequestRace(requestGetPatientsResourcesStatuses)),
  });

  yield put(resourcesInitializeComplete());
}

export function* onLoadResourceData({ payload: { resourceId } }) {
  yield put(resourcesResetFiles());
  const imageUrl = yield select(getDocumentImageUrlById(resourceId));
  const fileUrl = yield select(getDocumentFileUrlById(resourceId));

  if (imageUrl) {
    yield put(requestGetResourceImage({ url: imageUrl }));
  }
  if (fileUrl) {
    yield put(requestGetResourceFile({ url: fileUrl }));
  }

  const resourcesRace = mergeAll([
    imageUrl ? { getImage: race(createSagaRequestRace(requestGetResourceImage)) } : null,
    fileUrl ? { getFile: race(createSagaRequestRace(requestGetResourceFile)) } : null,
  ]);

  const { getImage, getFile } = yield all(resourcesRace);

  let isError = false;
  if (imageUrl) {
    isError = getImage.failure ? true : isError;
  }
  if (fileUrl) {
    isError = getFile.failure ? true : isError;
  }

  if (isError) {
    yield put(resourcesLoadDataFailure());
  } else {
    yield put(resourcesLoadDataSuccess({ resourceId }));
  }
}

export function* onOpenPdf() {
  const file = yield select(getResourceFile);
  const blob = b64toBlob(file, 'application/pdf');
  const blobUrl = URL.createObjectURL(blob);
  const pdfWindow = window.open(blobUrl);
  pdfWindow.document.title = i18n.t('common:resources.pdfPageTitle');
}

export function* onLikeResource({ payload: { like } }) {
  const changeStatuses = yield select(isStatusesInChange);
  if (changeStatuses || like === null) {
    return;
  }

  yield put(resourcesChangeStatuses());

  const statuses = yield select(getStatuses);
  const currentResource = yield select(getCurrentResource);

  const newStatuses = changeItemInStatuses({
    statuses,
    currentResource,
    prefix: 'like',
    answer: {
      valueBoolean: like,
    },
  });

  const newResource = path(['entry', '0', 'resource'])(newStatuses);

  yield put(
    requestPutPatientsStatuses({
      resourceId: newResource.id,
      resource: newResource,
    }),
  );
  const { success } = yield race(createSagaRequestRace(requestPutPatientsStatuses));
  if (success) {
    yield put(resourcesSetStatuses({ statuses: newStatuses }));
  }

  yield put(resourcesChangeStatusesComplete());
}

export function* onReadResource() {
  const changeStatuses = yield select(isStatusesInChange);
  if (changeStatuses) {
    return;
  }

  yield put(resourcesChangeStatuses());

  const statuses = yield select(getStatuses);
  const currentResource = yield select(getCurrentResource);

  const newStatuses = changeItemInStatuses({
    statuses,
    currentResource,
    prefix: 'read',
    answer: {
      valueDateTime: moment().format('YYYY-MM-DDThh:mm:ssZ'),
    },
  });

  const newResource = path(['entry', '0', 'resource'])(newStatuses);

  yield put(
    requestPutPatientsStatuses({
      resourceId: newResource.id,
      resource: newResource,
    }),
  );
  const { success } = yield race(createSagaRequestRace(requestPutPatientsStatuses));
  if (success) {
    yield put(resourcesSetStatuses({ statuses: newStatuses }));
  }

  yield put(resourcesChangeStatusesComplete());
}

export default function*() {
  yield takeEvery(resourcesInitialize, onResourcesInitialize);
  yield takeEvery(resourcesLoadData, onLoadResourceData);
  yield takeEvery(resourcesOpenPdf, onOpenPdf);
  yield takeEvery(resourcesLikeResource, onLikeResource);
  yield takeEvery(resourcesReadResource, onReadResource);
}
